import { type Database } from "@fitness-app/supabase";

/**
 * Content Studio / Klipownia (epic TRE-1975) — a single clip candidate mined from a long
 * recording, carried through render, QC and the human decision in the Studio panel.
 */

export type StudioClipEntity = Database["public"]["Tables"]["studio_clip"]["Row"];

export enum StudioClipStatus {
  /** Agent picked the fragment; render has not started yet. */
  Candidate = "candidate",
  Rendering = "rendering",
  RenderFailed = "render_failed",
  /** Rendered, but a blocking QC gate failed — visible, not approvable without an override. */
  QcFailed = "qc_failed",
  /** Rendered and QC-passed, awaiting the human decision. */
  Ready = "ready",
  Approved = "approved",
  Rejected = "rejected",
  Published = "published",
}

export enum StudioClipQcGate {
  Frame = "frame",
  Audio = "audio",
  Subtitles = "subtitles",
  Cut = "cut",
  Hook = "hook",
  Length = "length",
}

/** Gates 1–4 block the clip; hook and length only warn at approval time (TRE-1981). */
export const BLOCKING_QC_GATES: readonly StudioClipQcGate[] = [
  StudioClipQcGate.Frame,
  StudioClipQcGate.Audio,
  StudioClipQcGate.Subtitles,
  StudioClipQcGate.Cut,
];

export interface StudioClipQcGateResult {
  pass: boolean;
  reasoning: string;
  /** Position of the problem inside the clip, when the model could point at one. */
  timestampMs?: number | null;
}

export interface StudioClipQcResult {
  gates: Record<StudioClipQcGate, StudioClipQcGateResult>;
  /** True when every blocking gate passed. */
  pass: boolean;
  model: string;
}

/** Crop that followed the speaker (etap A). Coordinates are source pixels, times source ms. */
export interface StudioClipFraming {
  mode: "single" | "split" | "pad";
  reason: string;
  zoom: number;
  sourceWidth: number;
  sourceHeight: number;
  crop: { w: number; h: number };
  segments: { fromMs: number; toMs: number; tracks: { y: number; xKeyframes: { tMs: number; x: number }[] }[] }[];
  /** Deterministic check of the rendered frames, fed to the QC prompt as evidence. */
  gate?: {
    pass: boolean;
    reason: string;
    samples: { tMs: number; faceWidthRatio: number | null; eyeLineRatio: number | null }[];
  };
  /** Wall-clock split of the render, for sizing the machine. */
  timingsMs?: { detect?: number; ffmpeg?: number; captions?: number; gate?: number };
}

export interface StudioClipRenderMeta {
  durationMs: number;
  width: number;
  height: number;
  renderTimeMs: number;
  /** Segments actually rendered after silence trimming, for debugging cut decisions. */
  segments: { fromMs: number; toMs: number }[];
  /** Engine that painted the captions: "remotion", or "ass" when the fallback kicked in. */
  captionEngine?: string;
  framing?: StudioClipFraming;
  /** Measured loudness of the finished clip (etap C1): platform target is -14 LUFS / -1.5 dBTP. */
  audio?: { integratedLufs: number; loudnessRange: number; truePeakDb: number };
  /** Hook-first checks (etap D): when the first spoken word lands and whether a preface was cut. */
  hook?: { firstWordMs: number; prefaceCutMs: number; buttonAddedMs: number };
  /** Music bed mixed under the voice (etap C2), when the library had one. */
  music?: { key: string; title: string; startMs: number; gainDb: number };
}

/** One corrected caption word, keyed by its start time in the SOURCE recording. */
export interface StudioClipCaptionEdit {
  wordStartMs: number;
  text: string;
}

/**
 * Human decision from the Studio editor (etap F1): a tighter window and caption fixes.
 * The agent's startMs/endMs stay on the row as the original proposal; the render uses
 * the edit when present. Written only by the re-render endpoint (service role).
 */
export interface StudioClipEdit {
  startMs: number;
  endMs: number;
  captionEdits: StudioClipCaptionEdit[];
}

/** Role of a mined segment in an ad (etap G): every ad is hook → value → call to action. */
export enum StudioSegmentRole {
  Hook = "hook",
  Value = "value",
  Cta = "cta",
}

export type StudioSegmentEntity = Database["public"]["Tables"]["studio_segment"]["Row"];

export interface StudioSegment extends Omit<StudioSegmentEntity, "role"> {
  role: StudioSegmentRole;
}

/**
 * Opening type of a clip (etap D), with the ACQ kit's measured win-rate lift as the ranking
 * prior — contrarian openings win ~4× the base rate, questions ~0.2×.
 */
export enum StudioHookType {
  Contrarian = "contrarian",
  CutToPayoff = "cut_to_payoff",
  ExtremeNumber = "extreme_number",
  DirectAddress = "direct_address",
  Principle = "principle",
  Anecdote = "anecdote",
  BoldClaim = "bold_claim",
  HowTo = "how_to",
  Question = "question",
  Other = "other",
}

export const HOOK_TYPE_LIFT: Record<StudioHookType, number> = {
  [StudioHookType.Contrarian]: 3.99,
  [StudioHookType.CutToPayoff]: 1.68,
  [StudioHookType.ExtremeNumber]: 1.43,
  [StudioHookType.DirectAddress]: 1.28,
  [StudioHookType.Principle]: 1.0,
  [StudioHookType.Anecdote]: 0.97,
  [StudioHookType.BoldClaim]: 0.96,
  [StudioHookType.HowTo]: 0.66,
  [StudioHookType.Question]: 0.18,
  [StudioHookType.Other]: 0.12,
};

export const HOOK_TYPE_LABELS: Record<StudioHookType, string> = {
  [StudioHookType.Contrarian]: "kontra",
  [StudioHookType.CutToPayoff]: "od razu puenta",
  [StudioHookType.ExtremeNumber]: "liczba",
  [StudioHookType.DirectAddress]: "do widza",
  [StudioHookType.Principle]: "zasada",
  [StudioHookType.Anecdote]: "historia",
  [StudioHookType.BoldClaim]: "mocna teza",
  [StudioHookType.HowTo]: "instrukcja",
  [StudioHookType.Question]: "pytanie",
  [StudioHookType.Other]: "inne",
};

export enum StudioClipKind {
  /** One continuous window picked by the agent (or trimmed by a human). */
  Clip = "clip",
  /** An ad composed from segments with roles; `parts` holds the ordered source ranges. */
  Variant = "variant",
}

export interface StudioClipPart {
  role: StudioSegmentRole;
  segmentId: string;
  startMs: number;
  endMs: number;
}

export interface StudioClip extends Omit<
  StudioClipEntity,
  "qc" | "renderMeta" | "status" | "edit" | "kind" | "parts" | "hookType"
> {
  status: StudioClipStatus;
  qc: StudioClipQcResult | null;
  renderMeta: StudioClipRenderMeta | null;
  edit: StudioClipEdit | null;
  kind: StudioClipKind;
  parts: StudioClipPart[] | null;
  hookType: StudioHookType | null;
}

/** Source ranges the render concatenates, in output order: the parts of a variant, or the single window. */
export const getStudioClipRanges = (
  clip: Pick<StudioClip, "startMs" | "endMs" | "edit"> & { parts?: StudioClipPart[] | null },
): { startMs: number; endMs: number }[] => {
  if (clip.parts?.length) return clip.parts.map(({ startMs, endMs }) => ({ startMs, endMs }));
  return [getStudioClipWindow(clip)];
};

/** Window the render actually uses: the human edit when present, the agent proposal otherwise. */
export const getStudioClipWindow = (clip: Pick<StudioClip, "startMs" | "endMs" | "edit">) => ({
  startMs: clip.edit?.startMs ?? clip.startMs,
  endMs: clip.edit?.endMs ?? clip.endMs,
});

/** agentType of the parent ai_agent_request row created by the produce-video-clips task. */
export const STUDIO_CLIPS_AGENT_TYPE = "studio_clips";
